import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import { useLanguage } from '../../../../contexts/LanguageContext';
import LmsNewsPhoto from '../../lms-news/components/LmsNewsPhoto';
import LmsNewsBadges from '../../lms-news/components/LmsNewsBadges';
import { formatLocalDateTime, formatTrainingDateRange, getNewsTitle, isAnnouncement } from '../../lms-news/utils/newsUtils';
import DashboardPanel, { PanelLink } from './DashboardPanel';
import { ACCENT } from '../utils/dashboardUtils';

const AUTO_PLAY_MS = 6000;

const MetaItem = ({ icon, text }) => (
    <span className="flex align-items-center gap-2 text-sm" style={{ color: '#344054', minWidth: 0 }}>
        <i className={`pi ${icon}`} style={{ fontSize: '0.9rem' }} />
        <span className="white-space-nowrap overflow-hidden text-overflow-ellipsis">{text}</span>
    </span>
);

const NewsHighlights = ({ news, isLoading }) => {
    const { th } = useLanguage();
    const [index, setIndex] = useState(0);

    useEffect(() => {
        if (news.length < 2) return undefined;
        const timer = setTimeout(() => setIndex((prev) => (prev + 1) % news.length), AUTO_PLAY_MS);
        return () => clearTimeout(timer);
    }, [news.length, index]);

    const current = news[Math.min(index, news.length - 1)];
    const detailPath = current ? `/learning-management-system/news/${current.id}` : null;

    return (
        <DashboardPanel title={th('lmsDashboard.news.title')} action={<PanelLink to="/learning-management-system/news" label={th('lmsDashboard.viewAll')} />}>
            {isLoading && (
                <div className="flex flex-column gap-3">
                    <Skeleton height="12rem" borderRadius="0.75rem" />
                    <Skeleton width="80%" height="1.25rem" />
                    <Skeleton width="60%" />
                </div>
            )}

            {!isLoading && !current && (
                <div className="flex flex-column align-items-center justify-content-center gap-2 p-5 text-color-secondary flex-grow-1">
                    <i className="pi pi-megaphone" style={{ fontSize: '2rem' }} />
                    <span>{th('lmsDashboard.news.empty')}</span>
                </div>
            )}

            {!isLoading && current && (
                <div className="flex flex-column gap-3 flex-grow-1">
                    <Link to={detailPath} className="block no-underline">
                        <LmsNewsPhoto news={current} className="border-round-xl" style={{ aspectRatio: '16 / 9' }}>
                            <LmsNewsBadges news={current} className="absolute bottom-0 left-0 m-3" />
                        </LmsNewsPhoto>
                    </Link>

                    <Link to={detailPath} className="no-underline font-bold" style={{ color: '#101828', fontSize: '1.1rem', lineHeight: 1.35 }}>
                        {getNewsTitle(current)}
                    </Link>

                    {current.shortDescription && (
                        <span className="text-sm" style={{ color: '#475467', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {current.shortDescription}
                        </span>
                    )}

                    <div className="flex align-items-center gap-3 flex-wrap">
                        {isAnnouncement(current) ? (
                            current.publishDate && <MetaItem icon="pi-calendar" text={formatLocalDateTime(current.publishDate)} />
                        ) : (
                            <>
                                {current.planningLocationText && <MetaItem icon="pi-map-marker" text={current.planningLocationText} />}
                                {current.startDate && <MetaItem icon="pi-calendar" text={formatTrainingDateRange(current)} />}
                                {current.trainingCapacity != null && <MetaItem icon="pi-users" text={th('lmsNews.participants', { count: current.trainingCapacity })} />}
                            </>
                        )}
                    </div>

                    <div className="flex align-items-center justify-content-between mt-auto pt-2">
                        <div className="flex align-items-center gap-2">
                            {news.map((item, i) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    aria-label={`${i + 1}`}
                                    onClick={() => setIndex(i)}
                                    className="p-0 border-none cursor-pointer border-circle"
                                    style={{ width: '0.55rem', height: '0.55rem', backgroundColor: i === index ? ACCENT : '#D0D5DD' }}
                                />
                            ))}
                        </div>
                        <PanelLink to={detailPath} label={th('lmsDashboard.news.readMore')} />
                    </div>
                </div>
            )}
        </DashboardPanel>
    );
};

export default NewsHighlights;
